import React, { useEffect, useState } from 'react';
import { Box, Typography, useRightSideBar } from '@p81-common/ui';
import { createClasses } from './styles';

export interface WizardStep {
  content: React.ReactNode;
  isValid: boolean;
  nextLabel?: string;
  subtitle?: string;
}

export interface AssociationWizardProps {
  steps: WizardStep[];
  nextLabel: string;
  previousLabel: string;
  finishLabel: string;
  onBack: () => void;
  onFinish: () => void;
  isFinishing?: boolean;
  /**
   * Overrides the step-0 label. The button always calls onBack, so this is a
   * per-screen wording choice from the Figma spec, not a behaviour switch:
   * the MSSP Child screens label it "Cancel" (nodes 3:3329, 62:1739, 10:182)
   * while MSSP Parent - Associate Children labels it "Back" (272:1682) and so
   * passes nothing. Do not unify these without the design being updated first.
   */
  cancelLabel?: string;
  /** Lets the parent react to step changes (e.g. swap the page title). */
  onStepChange?: (stepIndex: number) => void;
}

const AssociationWizard = ({
  steps,
  nextLabel,
  previousLabel,
  finishLabel,
  onBack,
  onFinish,
  isFinishing = false,
  cancelLabel,
  onStepChange
}: AssociationWizardProps) => {
  const { setShowBottomActionBar } = useRightSideBar();
  const [currentStep, setCurrentStep] = useState(0);
  const classes = createClasses();

  useEffect(() => {
    onStepChange?.(currentStep);
  }, [currentStep, onStepChange]);

  const isLast = currentStep === steps.length - 1;
  const isCurrentValid = steps[currentStep].isValid;
  const currentNextLabel = steps[currentStep].nextLabel ?? nextLabel;

  useEffect(() => {
    setShowBottomActionBar({
      okButtonProps: {
        text: isLast ? finishLabel : currentNextLabel,
        disabled: !isCurrentValid || (isLast && isFinishing),
        onClick: isLast ? onFinish : () => setCurrentStep(s => s + 1)
      },
      // cancelButtonProps is the framework's name for the Previous-button slot.
      cancelButtonProps: {
        text: currentStep === 0 ? cancelLabel ?? previousLabel : previousLabel,
        onClick: () =>
          currentStep === 0 ? onBack() : setCurrentStep(s => s - 1)
      },
      isSideBar: true
    });

    return () => setShowBottomActionBar(null);
  }, [
    currentStep,
    isLast,
    isCurrentValid,
    isFinishing,
    currentNextLabel,
    finishLabel,
    previousLabel,
    onBack,
    onFinish,
    setShowBottomActionBar,
    cancelLabel
  ]);

  return (
    <Box className={classes.container}>
      {steps[currentStep].subtitle && (
        <Typography variant="subtitle1" className={classes.subtitle}>
          {steps[currentStep].subtitle}
        </Typography>
      )}
      {steps[currentStep].content}
    </Box>
  );
};

export default AssociationWizard;
